import { HttpInterceptorFn } from '@angular/common/http';
import {
  USE_SEO_BASE,
  USE_STATS_BASE,
  USE_MODEL_AUTOMATION_BASE,
  USE_ADS_BASE,
  USE_GOLIVE_BASE,
  USE_BUGS_BASE,
  FORCE_REGION,
} from '../core/http/http-context.tokens';
import { inject } from '@angular/core';
import { AuthTokenService } from '../core/services/auth-token.service';
import { getRegion, pickEnv } from '../core/http/env-utils';

export const BaseUrlInterceptor: HttpInterceptorFn = (req, next) => {
  const authTokenService = inject(AuthTokenService);
  const regionOverride = req.context.get(FORCE_REGION);
  const env = pickEnv(regionOverride ?? getRegion());

  const join = (base: string, path: string) => {
    const p = path.startsWith('/') ? path : `/${path}`;
    return `${base}${p}`;
  };

  const forceAppBase =
    req.url.includes('/screenshots/upload') ||
    req.url.includes('/performanceOverview/ask-ai') ||
    req.url.includes('/recorder/ask-ai');

  // Only force app base for relative URLs (avoid hijacking absolute URLs)
  if (forceAppBase && !/^https?:\/\//i.test(req.url)) {
    return next(req.clone({ url: join(env.address, req.url) }));
  }

  // SEO (Organic)
  if (req.context.get(USE_SEO_BASE)) {
    return next(req.clone({ url: join(env.seo, req.url) }));
  }

  // Statistics
  if (req.context.get(USE_STATS_BASE)) {
    return next(req.clone({ url: join(env.statistics, req.url) }));
  }

  // Model Automation
  if (req.context.get(USE_MODEL_AUTOMATION_BASE)) {
    return next(req.clone({ url: join(env.modelautomation, req.url) }));
  }

  // Ads
  if (req.context.get(USE_ADS_BASE)) {
    return next(req.clone({ url: join(env.ads, req.url) }));
  }

  // Golive
  if (req.context.get(USE_GOLIVE_BASE)) {
    return next(req.clone({ url: join(env.golive, req.url) }));
  }

  // Bugs project (Diagnostics)
  if (req.context.get(USE_BUGS_BASE)) {
    const original = req.url;

    const isSharable =
      original.startsWith('sharableBug/') || original.includes('/sharableBug/');

    let path = original;
    let headers = req.headers
      .set('Content-Type', 'application/json')
      .set('Accept', 'application/json');

    if (isSharable) {
      // token must be provided as `?token=...` in the URL; use bugShareableToken prefix
      const m = original.match(/[?&]token=([^&]+)/);
      const shareToken = m ? m[1] : '';
      headers = headers.set('Authorization', `bugShareableToken:${shareToken}`);
      // strip `sharableBug` and remove the token query parameter from the path sent to the API
      path = original
        .replace('sharableBug', '')
        .replace(/\?token=[^&]*/, '')
        .replace(/&token=[^&]*/, '');
    } else {
      const authKey = authTokenService.getToken() || '';
      headers = headers.set('Authorization', `token:${authKey}`);
    }

    return next(req.clone({ url: join(env.bugsProject, path), headers }));
  }

  // Default base for relative API calls (skip absolute URLs and assets)
  const isAbsolute = /^https?:\/\//i.test(req.url);
  const isAsset =
    req.url.startsWith('/assets/') || req.url.startsWith('assets/');

  if (isAbsolute || isAsset) {
    return next(req);
  }

  // If no special base is set and it's a relative non-asset URL, use the app base
  return next(req.clone({ url: join(env.address, req.url) }));
};
